// Small helpers that reproduce the Python semantics the QA port depends on, so
// the TS certificate is byte-compatible with tools/qa/qa_session.py output.
// Pure (no node imports) — this module is shared with the browser build.

/** Python3 round(): round-half-to-even, to `n` decimal places. */
export function pyRound(x: number, n: number): number {
  const m = 10 ** n;
  const scaled = x * m;
  const floor = Math.floor(scaled);
  const frac = scaled - floor;
  let r: number;
  // Ties (exactly .5 after scaling) round to even; everything else to nearest.
  if (Math.abs(frac - 0.5) < 1e-9) {
    r = floor % 2 === 0 ? floor : floor + 1;
  } else {
    r = Math.round(scaled);
  }
  return r / m;
}

/** Python f-string `{x:.<dec>f}`. */
export function fmtF(x: number, dec: number): string {
  return x.toFixed(dec);
}

/** Python f-string `{x:.<dec>%}` (value is a fraction; rendered as a percent). */
export function fmtPct(x: number, dec: number): string {
  return (x * 100).toFixed(dec) + "%";
}

/** Python str(float): integral floats render with a trailing ".0" (e.g. 6.0 -> "6.0"). */
export function pyFloat(x: number): string {
  return Number.isInteger(x) ? x.toFixed(1) : String(x);
}

/** Parse an int64-LE .timestamps sidecar into a JS number[] (ns fit in a double). */
export function parseInt64LE(buf: Uint8Array): number[] {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const n = Math.floor(buf.byteLength / 8);
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) out[i] = Number(view.getBigInt64(i * 8, true));
  return out;
}

export function sum(xs: number[]): number {
  let s = 0;
  for (const x of xs) s += x;
  return s;
}

/** Population standard deviation (matches scorecard.py `std`). */
export function std(xs: number[]): number {
  if (xs.length < 2) return 0;
  const mu = sum(xs) / xs.length;
  return Math.sqrt(sum(xs.map((x) => (x - mu) ** 2)) / xs.length);
}

export function dist3(a: number[], b: number[]): number {
  return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);
}
